/**
 * `/itemPublic/category` — hiyerarşi `parentId` ile gelir.
 * Kök: parentId null / undefined / 0 / ''.
 */

export function isRootCategory(cat) {
  if (!cat) return false;
  const p = cat.parentId;
  return p == null || p === '' || p === 0 || p === '0';
}

/** Üst seviye kategoriler (parentId yok). */
export function getRootCategories(categories) {
  if (!Array.isArray(categories)) return [];
  return categories.filter(isRootCategory);
}

export function findCategoryById(categories, id) {
  const n = Number(id);
  if (!Number.isFinite(n) || !Array.isArray(categories)) return null;
  return categories.find((c) => Number(c.id) === n) || null;
}

/** Belirli parentId altındaki doğrudan çocuklar. */
export function getChildCategories(categories, parentId) {
  if (!Array.isArray(categories)) return [];
  if (parentId == null || parentId === '') {
    return getRootCategories(categories);
  }
  const pid = Number(parentId);
  if (!Number.isFinite(pid)) return [];
  return categories.filter((c) => !isRootCategory(c) && Number(c.parentId) === pid);
}

/**
 * Seçili kategoriden köke kadar zincir (kök → … → seçili).
 * @returns {Array<object>}
 */
export function getCategoryAncestorChain(categories, categoryId) {
  const chain = [];
  let current = findCategoryById(categories, categoryId);
  const guard = new Set();
  while (current && !guard.has(Number(current.id))) {
    guard.add(Number(current.id));
    chain.unshift(current);
    if (isRootCategory(current)) break;
    current = findCategoryById(categories, current.parentId);
  }
  return chain;
}

/**
 * Nav satırları: her seviye için { parentId, items }.
 * Seçim yoksa yalnızca kök satırı.
 * Seçim varsa: kökler + seçim zincirindeki her düğümün çocukları (varsa).
 */
export function getCategoryNavLevels(categories, selectedId) {
  const roots = getRootCategories(categories);
  const levels = [{ parentId: null, items: roots }];

  if (selectedId == null || selectedId === '') return levels;

  const chain = getCategoryAncestorChain(categories, selectedId);
  for (const node of chain) {
    const children = getChildCategories(categories, node.id);
    if (children.length > 0) {
      levels.push({ parentId: node.id, items: children });
    }
  }
  return levels;
}

/** Seçili id, candidate veya onun altındaysa true (nav active için). */
export function isCategoryInSelection(categories, selectedId, candidateId) {
  if (selectedId == null || selectedId === '') return false;
  const selected = Number(selectedId);
  const candidate = Number(candidateId);
  if (!Number.isFinite(selected) || !Number.isFinite(candidate)) return false;
  if (selected === candidate) return true;
  return collectCategoryIdSet(categories, candidate).has(selected);
}

/**
 * Verilen kategori id’si ve tüm alt kategorilerinin id kümesi.
 * Madde filtrelerken üst kategori seçildiğinde altlara düşen kayıtları da kapsar.
 */
export function collectCategoryIdSet(categories, rootId) {
  const id = Number(rootId);
  if (!Number.isFinite(id)) return new Set();

  const byParent = new Map();
  for (const cat of categories || []) {
    if (isRootCategory(cat)) continue;
    const pid = Number(cat.parentId);
    if (!Number.isFinite(pid)) continue;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(Number(cat.id));
  }

  const result = new Set([id]);
  const stack = [id];
  while (stack.length > 0) {
    const current = stack.pop();
    const children = byParent.get(current) || [];
    for (const childId of children) {
      if (!result.has(childId)) {
        result.add(childId);
        stack.push(childId);
      }
    }
  }
  return result;
}

/** Madde categoryId’si seçilen kategori (veya altı) ile eşleşiyor mu? */
export function itemMatchesCategory(itemCategoryId, categoryIdSet) {
  if (!categoryIdSet || categoryIdSet.size === 0) return false;
  return categoryIdSet.has(Number(itemCategoryId));
}

/** Kategori adından türetilen genel / zayıf kelimeler — başlık eşlemesinde kullanılmaz. */
const CATEGORY_KEYWORD_STOP = new Set([
  've', 'ile', 'veya', 'vb', 'gibi', 'olan', 'için', 'icin',
  'and', 'the', 'of', 'such', 'as', 'based', 'dishes', 'food', 'type', 'types',
  'hamur', 'iş', 'işler', 'işleri', 'yemek', 'yemekler', 'yemekleri',
  'çeşit', 'çeşitler', 'tarif', 'tarifler', 'ürün', 'ürünler',
  'malzeme', 'malzemeler', 'geleneksel', 'taze', 'mevsim', 'karışık',
  'tip', 'tipi', 'kırmızı', 'beyaz', 'ana', 'ev', 'hafif', 'tane',
  'sebze', 'meyve', 'meyveli', 'hamurlu', 'soğuk', 'sıcak',
]);

function stemTurkishToken(token) {
  let s = String(token || '').toLocaleLowerCase('tr');
  const suffixes = ['ları', 'leri', 'ların', 'lerin', 'lara', 'lere', 'lar', 'ler'];
  for (const suf of suffixes) {
    if (s.length > suf.length + 2 && s.endsWith(suf)) {
      s = s.slice(0, -suf.length);
      break;
    }
  }
  return s;
}

function keywordsFromCategoryName(name) {
  if (!name) return [];
  return String(name)
    .toLocaleLowerCase('tr')
    .replace(/[()]/g, ' ')
    .split(/[\s,/&+.-]+/)
    .map(stemTurkishToken)
    .filter((t) => t.length >= 3 && !CATEGORY_KEYWORD_STOP.has(t));
}

/**
 * Seçilen kategori + alt dallarının adlarından anahtar kelimeler.
 * CMS henüz maddeyi alt categoryId’ye bağlamadıysa başlık eşlemesi için.
 */
export function collectCategoryKeywords(categories, selectedId) {
  const idSet = collectCategoryIdSet(categories, selectedId);
  const words = new Set();
  for (const cat of categories || []) {
    if (!idSet.has(Number(cat.id))) continue;
    for (const kw of keywordsFromCategoryName(cat.name)) {
      words.add(kw);
    }
  }
  return [...words];
}

/**
 * Madde listesini kategoriye göre filtreler.
 * 1) categoryId doğrudan / alt ağaçta eşleşirse onu kullanır.
 * 2) Yoksa (alt dallarda henüz kayıt yoksa) kök havuzunda başlıkta
 *    kategori adı anahtar kelimeleriyle daraltır — tüm kökü geri vermez.
 */
export function filterItemsByCategory(items, categories, selectedId) {
  if (!Array.isArray(items)) return [];
  if (selectedId == null || selectedId === '') return items;

  const strictSet = collectCategoryIdSet(categories, selectedId);
  const strict = items.filter((item) => itemMatchesCategory(item.categoryId, strictSet));
  if (strict.length > 0) return strict;

  const chain = getCategoryAncestorChain(categories, selectedId);
  const root = chain[0];
  if (!root) return [];

  const rootSet = collectCategoryIdSet(categories, root.id);
  const pool = items.filter((item) => itemMatchesCategory(item.categoryId, rootSet));
  const keywords = collectCategoryKeywords(categories, selectedId);
  if (keywords.length === 0) return [];

  return pool.filter((item) => {
    const title = String(item.title || '').toLocaleLowerCase('tr');
    return keywords.some((kw) => title.includes(kw));
  });
}

/** Şehir (veya başka) süzülmüş listede bu kategoride madde var mı? */
export function categoryHasMatchingItems(items, categories, categoryId) {
  return filterItemsByCategory(items, categories, categoryId).length > 0;
}
